import React, { useState, useEffect } from 'react';
import { Input, Select, Row, Col, Button, DatePicker } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import moment, { Moment } from 'moment';

const { Option } = Select;
const { RangePicker } = DatePicker;

const StyledRow = styled(Row)`
  margin-bottom: 16px;
  @media (max-width: 768px) {
    margin-bottom: 12px;
  }
`;

const StyledInput = styled(Input)`
  width: 200px;
  font-size: 13px;
  @media (max-width: 768px) {
    width: 100%;
  }
  &::placeholder {
    font-size: 13px;
  }
`;

const StyledSelect = styled(Select)`
  width: 200px;
  font-size: 13px;
  font-weight: normal;
  @media (max-width: 768px) {
    width: 100%;
  }
  .ant-select-selection-placeholder {
    font-size: 13px;
  }
`;

const StyledDatePicker = styled(RangePicker)`
  width: 250px;
  font-size: 13px !important;

  .ant-picker-input input {
    font-size: 13px !important;
  }

  .ant-picker-input::placeholder {
    font-size: 13px !important;
  }
`;

const StyledButton = styled(Button)`
  width: 40px;
  @media (max-width: 768px) {
    width: 100%;
  }
`;

interface FilterComponentProps {
  data: {
    name: string;
    placeholder: string;
    type: 'text' | 'dropdown' | 'daterange';
    options?: Record<string, { label: string; value: string }[]>;
    dependsOn?: string;
  }[];
  onFilter: (values: { [key: string]: string | [string, string] | null }) => void;
  defaultValues?: { [key: string]: string | null };
  dependsOn?: string;
}

export const FilterComponent: React.FC<FilterComponentProps> = ({ data, onFilter, defaultValues = {} }) => {
  const [formValues, setFormValues] = useState<{ [key: string]: string | [string, string] | null }>(() => {
    const initialValues = data.reduce((acc, item) => ({ ...acc, [item.name]: item.type === 'text' ? '' : null }), {});
    return { ...initialValues, ...defaultValues };
  });

  const handleInputChange = (name: string, value: string | [string, string] | null) => {
    setFormValues((prevValues) => ({ ...prevValues, [name]: value }));
  };

  const handleDateRangeChange = (name: string, dates: [Moment, Moment] | null) => {
    if (dates && dates[0] && dates[1]) {
      const dateRange: [string, string] = [dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')];
      handleInputChange(name, dateRange);
    } else {
      handleInputChange(name, null);
    }
  };

  const handleSearch = () => {
    const sanitizedValues = Object.fromEntries(Object.entries(formValues).map(([key, value]) => [key, value ?? null]));
    onFilter(sanitizedValues);
  };

  const handleReset = () => {
    const resetValues = data.reduce((acc, item) => ({ ...acc, [item.name]: item.type === 'text' ? '' : null }), {});
    const valueAfterReset = { ...resetValues, ...defaultValues };
    setFormValues(valueAfterReset);
    onFilter(valueAfterReset);
  };

  return (
    <StyledRow gutter={[16, 16]} justify="start" align="middle">
      {data.map((item) => {
        // 🔹 Conditional rendering based on dependency
        if (item.dependsOn && !formValues[item.dependsOn]) {
          return null;
        }

        return (
          <Col key={item.name}>
            {item.type === 'text' ? (
              <StyledInput
                placeholder={item.placeholder}
                allowClear
                prefix={<SearchOutlined style={{ marginRight: '5px' }} />}
                value={formValues[item.name] || ''}
                onChange={(e) => handleInputChange(item.name, e.target.value)}
                size="small"
              />
            ) : item.type === 'dropdown' && item.options ? (
              <StyledSelect
                placeholder={item.placeholder}
                value={formValues[item.name] || null}
                onChange={(value) => handleInputChange(item.name, (value as string) || null)}
                allowClear
                size="small"
              >
                {Object.values(item.options)
                  .flat()
                  .map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
              </StyledSelect>
            ) : item.type === 'daterange' ? (
              <StyledDatePicker
                placeholder={['From', 'To']}
                onChange={(dates) => handleDateRangeChange(item.name, dates as [Moment, Moment] | null)}
                value={(() => {
                  const val = formValues[item.name];
                  if (val && Array.isArray(val) && val.length === 2) {
                    return [moment(val[0]), moment(val[1])] as [Moment, Moment];
                  }
                  return undefined;
                })()}
                size="small"
              />
            ) : null}
          </Col>
        );
      })}

      <Col>
        <StyledButton type="primary" size="small" onClick={handleSearch}>
          <SearchOutlined />
        </StyledButton>
      </Col>

      <Col>
        <StyledButton size="small" onClick={handleReset} icon={<ReloadOutlined />} />
      </Col>
    </StyledRow>
  );
};

export default FilterComponent;
